import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingBag, Truck, Palette, Globe, CheckCircle, Circle } from 'lucide-react';
import { productsApi } from '../../../api/endpoints/products';
import { expensesApi } from '../../../api/endpoints/expenses';
import { Button } from '../../../components/ui/Button';
import type { StoreSettings, ThemeSettings } from '../../../types/settings';

interface ChecklistItem {
  key: string;
  icon: React.ReactNode;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  link: string;
  optional?: boolean;
  check: (ctx: CheckContext) => boolean;
}

interface CheckContext {
  productCount: number;
  settings?: StoreSettings;
}

// A store counts as "customized" only when the merchant has actually changed
// something - logo, primary color, or a theme button style - not when the
// default theme object simply exists on the settings document.
function isDesignCustomized(settings?: StoreSettings): boolean {
  if (!settings) return false;
  if (settings.logo) return true;
  const theme = settings.theme as ThemeSettings | undefined;
  if (!theme) return false;
  return !!(
    theme.primaryColor ||
    theme.accentColor ||
    theme.buttonColor ||
    theme.customCss ||
    theme.headingFont ||
    theme.bodyFont ||
    theme.customFontUrl
  );
}

export function WelcomeTab({ settings }: { settings?: StoreSettings; storeId: string }) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: productsData } = useQuery({
    queryKey: ['products-count', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 1 }),
    enabled: !!storeId,
  });

  // Custom domain is a paid-plan capability - hide the step for free/starter
  // stores so the checklist doesn't dangle an action they can't complete.
  const { data: membership } = useQuery({
    queryKey: ['membership-status', storeId],
    queryFn: () => expensesApi.getMembershipStatus(storeId!),
    enabled: !!storeId,
  });
  const isPaidPlan = !!membership && membership.plan !== 'free' && !membership.isExpired;

  const checklist: ChecklistItem[] = [
    {
      key: 'products',
      icon: <ShoppingBag className="h-5 w-5" />,
      titleKey: 'settingsWelcome.addProducts',
      titleFallback: 'Add Your Products',
      descKey: 'settingsWelcome.addProductsDesc',
      descFallback: 'Create your first product with images, pricing, and descriptions.',
      link: `/${storeId}/products`,
      check: (ctx) => ctx.productCount > 0,
    },
    {
      key: 'design',
      icon: <Palette className="h-5 w-5" />,
      titleKey: 'settingsWelcome.customizeDesign',
      titleFallback: 'Customize Store Design',
      descKey: 'settingsWelcome.customizeDesignDesc',
      descFallback: 'Personalize your theme, colors, and navigation.',
      link: `/${storeId}/design`,
      check: (ctx) => isDesignCustomized(ctx.settings),
    },
    {
      key: 'shipping',
      icon: <Truck className="h-5 w-5" />,
      titleKey: 'settingsWelcome.setupShipping',
      titleFallback: 'Set Up Shipping',
      descKey: 'settingsWelcome.setupShippingDesc',
      descFallback: 'Optional: only needed if you ship physical products.',
      link: `/${storeId}/shipping`,
      optional: true,
      check: () => false,
    },
    ...(isPaidPlan
      ? [{
          key: 'domain',
          icon: <Globe className="h-5 w-5" />,
          titleKey: 'settingsWelcome.addDomain',
          titleFallback: 'Add a Custom Domain',
          descKey: 'settingsWelcome.addDomainDesc',
          descFallback: 'Connect your own domain for a professional storefront.',
          link: `/${storeId}/settings`,
          optional: true,
          check: (ctx: CheckContext) => !!(ctx.settings?.customDomain || ctx.settings?.domain),
        } as ChecklistItem]
      : []),
  ];

  const ctx: CheckContext = {
    productCount: productsData?.total ?? 0,
    settings,
  };

  // Progress is measured against required steps only - optional items
  // (shipping, custom domain) shouldn't hold back the 100% indicator.
  const requiredItems = checklist.filter((item) => !item.optional);
  const completedCount = useMemo(
    () => checklist.filter((item) => item.check(ctx)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.productCount, ctx.settings, isPaidPlan],
  );
  const requiredCompleted = requiredItems.filter((item) => item.check(ctx)).length;
  const progress = requiredItems.length > 0
    ? Math.round((requiredCompleted / requiredItems.length) * 100)
    : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Welcome message */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 text-center">
        <h2 className="text-lg font-bold mb-1">
          {t('settingsWelcome.title', 'Welcome to your store!')}
        </h2>
        <p className="text-sm text-text-muted">
          {t('settingsWelcome.subtitle', 'Complete the setup steps below to start selling.')}
        </p>

        {/* Progress bar */}
        <div className="mt-5 mx-auto max-w-md">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-text-secondary">
              {t('settingsWelcome.progress', 'Setup Progress')}
            </span>
            <span className="text-xs font-semibold text-accent">{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-bg-tertiary overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-text-muted">
            {completedCount}/{checklist.length} {t('settingsWelcome.completed', 'completed')}
          </p>
        </div>
      </section>

      {/* Checklist */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-4">
          {t('settingsWelcome.checklist', 'Setup Checklist')}
        </h2>
        <div className="space-y-3">
          {checklist.map((item) => {
            const done = item.check(ctx);
            return (
              <div
                key={item.key}
                className={`flex items-start gap-4 rounded-lg border p-4 transition-colors ${
                  done
                    ? 'border-success/30 bg-success/5'
                    : 'border-border-default bg-bg-primary'
                }`}
              >
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  done ? 'bg-success/10 text-success' : 'bg-bg-tertiary text-text-muted'
                }`}>
                  {done ? <CheckCircle className="h-5 w-5" /> : item.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${done ? 'text-success line-through' : ''}`}>
                      {t(item.titleKey, item.titleFallback)}
                    </p>
                    {item.optional && !done && (
                      <span className="rounded-full border border-border-default bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-muted">
                        {t('settingsWelcome.optional', 'Optional')}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-text-muted">
                    {t(item.descKey, item.descFallback)}
                  </p>
                </div>
                {!done && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(item.link)}
                    className="shrink-0"
                  >
                    {t('settingsWelcome.start', 'Start')}
                  </Button>
                )}
                {done && (
                  <Circle className="h-5 w-5 shrink-0 text-success fill-success" />
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
